import { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { fmtMoney, fmtNum, fmtDateTime } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { DataTable } from "../components/DataTable";
import { btnPrimary, selectCls } from "../components/Modal";
import { toast } from "sonner";
import { ClipboardCheck } from "lucide-react";

export default function Inventory() {
  const { user } = useAuth();
  const cur = user?.currency || "FCFA";
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [products, setProducts] = useState([]);
  const [counts, setCounts] = useState({});
  const [result, setResult] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/warehouses").then((r) => { setWarehouses(r.data); if (r.data[0] && !warehouseId) setWarehouseId(r.data[0].id); }).catch(() => {});
    api.get("/products", { params: { status: "active", limit: 2000 } }).then((r) => setProducts(r.data.items)).catch((e) => toast.error(errMsg(e)));
    api.get("/inventory/sessions").then((r) => setSessions(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const lines = products.map((p) => {
    const system = (p.stock_by_warehouse || {})[warehouseId] || 0;
    const physical = counts[p.id] !== undefined && counts[p.id] !== "" ? Number(counts[p.id]) : system;
    return { ...p, system, physical, diff: physical - system };
  });
  const changed = lines.filter((l) => Math.abs(l.diff) > 1e-9);

  const validate = async () => {
    setSaving(true);
    try {
      const payload = { warehouse_id: warehouseId, counts: lines.map((l) => ({ product_id: l.id, physical: l.physical })) };
      const r = await api.post("/inventory", payload);
      setResult(r.data);
      setCounts({});
      toast.success(`Inventaire ${r.data.number} validé — ${r.data.lines.filter((l) => l.diff !== 0).length} ajustement(s)`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const sessCols = [
    { key: "number", label: "N°", render: (s) => <span className="font-money font-bold">{s.number}</span> },
    { key: "created_at", label: "Date", render: (s) => fmtDateTime(s.created_at) },
    { key: "created_by", label: "Par" },
    { key: "total_variance_value", label: "Valeur écart", className: "text-right", render: (s) => (
      <span className={`font-money font-bold ${Math.abs(s.total_variance_value) > 0.01 ? "text-rose-600" : "text-emerald-600"}`}>{fmtMoney(s.total_variance_value, cur)}</span>) },
  ];

  return (
    <div className="space-y-4" data-testid="inventory-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Inventaire</h1>
        <select data-testid="inv-warehouse" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} className={`${selectCls} w-56`}>
          {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
      </div>

      <div className="bg-card border rounded-xl overflow-hidden">
        <div className="p-3 border-b text-sm text-muted-foreground">Comptez physiquement les produits, ajustez les quantités réelles puis validez. Les écarts créent des mouvements d'ajustement traçables.</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left text-xs font-bold uppercase text-muted-foreground">Produit</th>
                <th className="px-4 py-3 text-right text-xs font-bold uppercase text-muted-foreground">Stock système</th>
                <th className="px-4 py-3 text-right text-xs font-bold uppercase text-muted-foreground">Stock physique</th>
                <th className="px-4 py-3 text-right text-xs font-bold uppercase text-muted-foreground">Écart</th>
                <th className="px-4 py-3 text-right text-xs font-bold uppercase text-muted-foreground">Valeur écart</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.id} data-testid={`inv-row-${l.id}`} className="border-b last:border-0">
                  <td className="px-4 py-2 font-semibold">{l.name}</td>
                  <td className="px-4 py-2 text-right font-money">{fmtNum(l.system)} {l.unit}</td>
                  <td className="px-4 py-2 text-right">
                    <input data-testid={`inv-count-${l.id}`} type="number" step="any"
                      value={counts[l.id] !== undefined ? counts[l.id] : l.system}
                      onChange={(e) => setCounts({ ...counts, [l.id]: e.target.value })}
                      className="w-24 h-9 px-2 rounded-md border border-input bg-background font-money text-right text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
                  </td>
                  <td className={`px-4 py-2 text-right font-money font-bold ${l.diff > 0 ? "text-emerald-600" : l.diff < 0 ? "text-rose-600" : ""}`}>
                    {l.diff > 0 ? "+" : ""}{fmtNum(l.diff)}
                  </td>
                  <td className="px-4 py-2 text-right font-money text-muted-foreground">{l.purchase_price != null ? fmtMoney(l.diff * (l.purchase_price || 0), cur) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="p-3 border-t flex items-center justify-between">
          <span className="text-sm font-semibold" data-testid="inv-changed-count">{changed.length} écart(s) détecté(s)</span>
          <button data-testid="inv-validate" onClick={validate} disabled={saving || !warehouseId} className={btnPrimary}>
            <ClipboardCheck className="h-4 w-4" /> {saving ? "Validation..." : "Valider l'inventaire"}
          </button>
        </div>
      </div>

      {result && (
        <div className="bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4" data-testid="inv-result">
          <div className="font-heading font-bold text-emerald-800 dark:text-emerald-300">Inventaire {result.number} enregistré</div>
          <div className="text-sm text-emerald-700 dark:text-emerald-400">Valeur totale des écarts : <span className="font-money font-bold">{fmtMoney(result.total_variance_value, cur)}</span></div>
        </div>
      )}

      <h2 className="font-heading font-bold text-lg">Sessions d'inventaire</h2>
      <DataTable testid="inv-sessions" columns={sessCols} rows={sessions} searchKeys={["number", "created_by"]} empty="Aucune session" />
    </div>
  );
}
